import { useCallback } from "react";

import { SectionType } from "components/server-types";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { useAppSelector } from "store/index";
import { selectTemplateSections, termSelectors } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { isSectionArchived } from "utils/archiving";
import { isLinkedSection } from "utils/publishing";

export const useIsArchived = () => {
  const terms = useAppSelector(termSelectors.selectEntities);
  const templateSections = useListSelector(selectTemplateSections);

  const isArchived = useCallback(
    (section: SectionType) => isSectionArchived(section, terms, templateSections),
    [terms, templateSections],
  );

  const isActive = useCallback((section: SectionType) => !isArchived(section), [isArchived]);

  const sortByActiveTitles = useCallback(
    (a: SectionType, b: SectionType) => {
      const aArchived = isArchived(a);
      const bArchived = isArchived(b);

      if (aArchived && !bArchived) return 1;
      if (!aArchived && bArchived) return -1;

      // eslint-disable-next-line babel/new-cap
      return ORDER_BY_TITLE(a, b);
    },
    [isArchived],
  );

  const canUnarchive = useCallback(
    (section: SectionType) => {
      if (!isArchived(section)) return false;
      const term = section.term_id ? terms[section.term_id] : undefined;
      const templateSection = isLinkedSection(section)
        ? templateSections.find((s) => s.id === section.template_id)
        : undefined;
      return Boolean(!term?.archived_at && !templateSection?.archived_at);
    },
    [isArchived, terms, templateSections],
  );

  return { isArchived, isActive, sortByActiveTitles, canUnarchive };
};
